/** @format */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Transition } from "@headlessui/react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { useNotificationPreferences } from "@/hooks/useNotificationPreferences";
import { useNotificationToastStore } from "@/stores/notificationToastStore";
import { faXmark } from "@/utils/iconLibraryTreeShake";
import { getNotificationConfig } from "@/utils/notificationConfig";

const DEFAULT_DURATION = 8000;

const ToastCard = ({
  id,
  notification,
  duration,
  persistent,
}: {
  id: string;
  notification: {
    category: string;
    title: string;
    body: string;
    data: Record<string, unknown>;
  };
  duration: number;
  persistent: boolean;
}) => {
  const removeToast = useNotificationToastStore(state => state.removeToast);
  const navigate = useNavigate();
  const config = getNotificationConfig(
    notification.category,
    notification.data?.incidentType as string | undefined
  );

  const runId = notification.data?.runId as string | undefined;

  useEffect(() => {
    if (persistent) return;
    const timer = setTimeout(() => removeToast(id), duration);
    return () => clearTimeout(timer);
  }, [id, removeToast, duration, persistent]);

  const handleClick = () => {
    if (runId) {
      void navigate({ to: `/run/${runId}` });
    }
    removeToast(id);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeToast(id);
  };

  // Parse top_5 data for mini leaderboard
  const top5 = notification.data?.top5 as
    | Array<{ position: number; number: string; name: string }>
    | undefined;

  return (
    <button
      type="button"
      className="w-80 bg-slate-900/95 backdrop-blur-sm border border-slate-700/50 rounded-lg shadow-xl shadow-black/30 overflow-hidden cursor-pointer group text-left animate-toast-enter"
      style={
        {
          "--toast-accent": config.hex,
        } as React.CSSProperties
      }
      onClick={handleClick}
    >
      <div className="flex">
        {/* Category accent stripe */}
        <div
          className="w-1 shrink-0 rounded-l-lg"
          style={{ backgroundColor: config.hex }}
        />

        <div className="flex-1 min-w-0">
          {/* Progress bar */}
          {!persistent && (
            <div className="h-0.5 bg-slate-800">
              <div
                className="h-full rounded-full toast-progress"
                style={{
                  animationDuration: `${duration}ms`,
                  backgroundColor: config.hex,
                }}
              />
            </div>
          )}

          <div className="px-3 py-2.5 flex items-start gap-2.5">
            <FontAwesomeIcon
              icon={config.icon}
              className={`${config.color} mt-0.5 text-base`}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-100 leading-tight">
                {notification.title}
              </p>
              <p className="text-xs text-slate-400 mt-1 leading-snug line-clamp-2">
                {notification.body}
              </p>
              {/* Top-5 mini leaderboard */}
              {top5 && top5.length > 0 && (
                <p className="text-[10px] text-slate-500 mt-1.5 truncate font-mono">
                  {top5
                    .slice(0, 5)
                    .map(d => `${d.position}. #${d.number} ${d.name}`)
                    .join("  ")}
                </p>
              )}
              {/* Navigation hint */}
              {runId && (
                <p className="text-[10px] text-slate-600 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  View Race →
                </p>
              )}
            </div>
            <button
              type="button"
              className="text-slate-500 hover:text-slate-300 transition-colors opacity-0 group-hover:opacity-100 -mt-0.5"
              aria-label="Dismiss notification"
              onClick={handleDismiss}
            >
              <FontAwesomeIcon icon={faXmark} className="text-sm" />
            </button>
          </div>
        </div>
      </div>
    </button>
  );
};

const NotificationToast = () => {
  const toasts = useNotificationToastStore(state => state.toasts);
  const overflowCount = useNotificationToastStore(state => state.overflowCount);
  const addDemoToast = useNotificationToastStore(state => state.addDemoToast);
  const { preferences } = useNotificationPreferences();

  const duration = (preferences?.toastDisplaySeconds ?? 8) * 1000 || DEFAULT_DURATION;
  const persistent = preferences?.toastPersistent ?? false;

  // Dev preview: expose trigger on window for manual testing.
  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }
    (window as unknown as Record<string, unknown>).__galaxie_demo_toast = addDemoToast;
    return () => {
      delete (window as unknown as Record<string, unknown>).__galaxie_demo_toast;
    };
  }, [addDemoToast]);

  // Dev shortcut: Ctrl+Shift+N to preview a demo toast.
  useHotkeys(
    "ctrl+shift+n",
    () => addDemoToast(),
    { enabled: import.meta.env.DEV, enableOnFormTags: true, preventDefault: true },
    [addDemoToast]
  );

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map(toast => (
        <Transition
          key={toast.id}
          as="div"
          appear
          show
          className="pointer-events-auto"
          enter="duration-300 ease-out transition-all"
          enterFrom="translate-x-full opacity-0 scale-95"
          enterTo="translate-x-0 opacity-100 scale-100"
          leave="duration-200 ease-in transition-all"
          leaveFrom="translate-x-0 opacity-100"
          leaveTo="translate-x-full opacity-0"
        >
          <ToastCard
            id={toast.id}
            notification={toast.notification}
            duration={duration}
            persistent={persistent}
          />
        </Transition>
      ))}
      {overflowCount > 0 && toasts.length > 0 && (
        <div className="pointer-events-none text-center">
          <span className="text-xs text-slate-500">+{overflowCount} more</span>
        </div>
      )}
    </div>
  );
};

export default NotificationToast;
